import * as React from 'react';

import { Hidden } from '@coursera/cds-core';

import type {
  InCoursePageHeader_EnterpriseProgramFragment as EnterpriseProgram,
  InCoursePageHeader_NaptimeManagedThirdPartyOrganizationFragment as ThirdPartyOrganizationsV1,
} from 'bundles/composable-page-header-in-course/queries/__generated__/InCourseMyPageHeaderQuery';
import DefaultLogoImage from 'bundles/composable-page-header/components/grid-items/home-logo/DefaultLogoImage';
import DefaultOrganizationLogo from 'bundles/enterprise-legacy-learner-home/components/DefaultOrganizationLogo';

import _t from 'i18n!nls/composable-page-header-enterprise';

type LogoImageData = {
  src: string;
  alt: string;
};

type SquareAndRectangleLogoData = {
  squareLogoSrc: string;
  rectangularLogoSrc: string;
  alt: string;
};

// Links to program home e.g. https://www.coursera.org/programs/genai-academy-7izvo
export const getLinkForEnterpriseProgram = (program?: EnterpriseProgram | null) => {
  const slug = program?.slug;
  return slug ? `/programs/${slug}` : '/';
};

export const getLinkForOrgHome = (organization?: ThirdPartyOrganizationsV1 | null) => {
  const slug = organization?.slug;
  return slug ? `/organizations/${slug}` : '/';
};

export const getEnterpriseLogoLabel = (program?: EnterpriseProgram | null) => {
  return program ? _t('Coursera for #{programName}', { programName: program?.name }) : _t('Coursera');
};

export const getEnterpriseOrgLogoLabel = (organization?: ThirdPartyOrganizationsV1 | null) => {
  return organization
    ? _t('Coursera for #{organizationName}', { organizationName: organization?.name })
    : _t('Coursera');
};

const getOrgLogoData = (thirdPartyOrganization?: ThirdPartyOrganizationsV1): LogoImageData | undefined => {
  const thirdPartyOrgLogo = thirdPartyOrganization?.rectangularLogo || thirdPartyOrganization?.squareLogo;
  return thirdPartyOrgLogo ? { src: thirdPartyOrgLogo, alt: thirdPartyOrganization?.name } : undefined;
};

const getProgramLogoData = (enterpriseProgram?: EnterpriseProgram): SquareAndRectangleLogoData | undefined => {
  const squareSource = enterpriseProgram?.squareLogo || enterpriseProgram?.rectangularLogo;
  const rectangularSource = enterpriseProgram?.rectangularLogo || enterpriseProgram?.squareLogo;
  const alt = enterpriseProgram?.name || '';

  return squareSource && rectangularSource
    ? { squareLogoSrc: squareSource, rectangularLogoSrc: rectangularSource, alt }
    : undefined;
};

export const getEnterpriseProgramLogos = (enterpriseProgram: EnterpriseProgram) => {
  const logos: React.ReactNode[] = [];

  const thirdPartyOrgLogoData = getOrgLogoData(enterpriseProgram?.naptimeThirdPartyOrganization);
  if (thirdPartyOrgLogoData) {
    logos.push(
      <DefaultLogoImage
        src={thirdPartyOrgLogoData.src}
        alt={thirdPartyOrgLogoData.alt}
        key={thirdPartyOrgLogoData.src}
      />
    );
  } else {
    // TODO: We're using 32px for logo heights throughout the header - define a constant for this
    logos.push(
      <DefaultOrganizationLogo size={32} thirdPartyOrganization={enterpriseProgram?.naptimeThirdPartyOrganization} />
    );
  }

  const programLogoData = getProgramLogoData(enterpriseProgram);
  if (programLogoData) {
    logos.push(
      // Render the rectangular logo on large and up, and the square logo on medium and smaller
      <React.Fragment>
        <Hidden mdDown>
          <DefaultLogoImage src={programLogoData.rectangularLogoSrc} alt={programLogoData.alt} />
        </Hidden>
        <Hidden lgUp>
          <DefaultLogoImage src={programLogoData.squareLogoSrc} alt={programLogoData.alt} />
        </Hidden>
      </React.Fragment>
    );
  }
  return logos;
};

export const getEnterpriseOrgLogo = (thirdPartyOrganization: ThirdPartyOrganizationsV1) => {
  const thirdPartyOrgLogoData = getOrgLogoData(thirdPartyOrganization);
  if (thirdPartyOrgLogoData) {
    return (
      <DefaultLogoImage
        src={thirdPartyOrgLogoData.src}
        alt={thirdPartyOrgLogoData.alt}
        key={thirdPartyOrgLogoData.src}
      />
    );
  } else {
    // TODO: We're using 32px for logo heights throughout the header - define a constant for this
    return <DefaultOrganizationLogo size={32} thirdPartyOrganization={thirdPartyOrganization} />;
  }
};
